import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  signal,
  SimpleChanges,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Router } from '@angular/router';

import { fromEvent, map, Observable, Subscription } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { symbolToLangPath, unsubscribe, urlToPathname } from '@fcom/core/utils';

@Directive({
  selector: 'a[finLink]',
  standalone: false,
  host: {
    '[attr.href]': `href()`,
  },
})
export class LinkDirective implements OnChanges, OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private platform = inject(PLATFORM_ID);

  @Input()
  finLink: Observable<string> | string;
  @Input()
  external = false;
  @Input()
  openInNewTab = false;
  @Input()
  nofollow = false;

  @Output()
  afterNavigation: EventEmitter<Event> = new EventEmitter();
  @Output()
  beforeNavigation: EventEmitter<Event> = new EventEmitter();

  href = signal<string>(undefined);

  private linkVal: string;
  private clicks: Subscription;
  private subscription: Subscription;

  ngOnInit(): void {
    if (isPlatformServer(this.platform)) {
      return;
    }
    this.clicks = fromEvent(this.elementRef.nativeElement, 'click').subscribe((ev: KeyboardEvent) =>
      this.handleClick(ev)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['finLink']) {
      this.subscribeToLink(changes['finLink'].currentValue);
    }
  }

  legacySiteLinkLocale(url = ''): string {
    if (!url) {
      return url;
    }
    const ddsLocale = this.languageService.ddsLocaleValue;
    return symbolToLangPath(url, ddsLocale as Parameters<typeof symbolToLangPath>[1]);
  }

  subscribeToLink(link: Observable<string> | string): void {
    this.subscription = unsubscribe(this.subscription);
    if (link instanceof Observable) {
      this.subscription = link
        .pipe(map((d) => this.legacySiteLinkLocale(d)))
        .subscribe((link) => this.setLinkValue(link));
    } else {
      this.setLinkValue(this.legacySiteLinkLocale(link));
    }
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
    this.clicks = unsubscribe(this.clicks);
  }

  handleClick(event: KeyboardEvent): void {
    this.beforeNavigation.emit(event);
    if (!(event.ctrlKey || event.metaKey) && !this.external && !this.openInNewTab && !event.defaultPrevented) {
      event.stopPropagation();
      event.preventDefault();
      if (this.linkVal) {
        this.router.navigateByUrl(this.linkVal);
        this.afterNavigation.emit(event);
      }
    }
    // Cmd/Ctrl + click, opens in new tab on most browsers, do nothing
  }

  private setLinkValue = (link: string) => {
    const l = this.toActualLink(link);
    this.linkVal = l;
    this.href.set(l);
    if (this.openInNewTab) {
      this.elementRef.nativeElement.target = '_blank';
      // Target _blank handles this in new browsers but for backward compatibility we set this too still
      this.elementRef.nativeElement.rel = 'noopener';
      if (this.nofollow) {
        this.elementRef.nativeElement.rel = 'noopener nofollow';
      }
    }

    if (!this.openInNewTab && this.nofollow) {
      this.elementRef.nativeElement.rel = 'nofollow';
    }
  };

  private toActualLink = (l: string): string => (this.external ? l : urlToPathname(l, { preserveQueryParams: true }));
}
